import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, Trash2, Printer, Download, Check, X, Clock, ArrowUpDown, ArrowUp, ArrowDown, Search as SearchIcon, Upload as UploadIcon, Calendar, Filter, Activity, Ban } from 'lucide-react';
import VoucherUsageCheckModal from '../components/VoucherUsageCheckModal';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import VoucherPrintTemplate from '../components/VoucherPrintTemplate';
import MikrotikGatewaySelector from '../components/MikrotikGatewaySelector';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Vouchers = () => {
  const { token, user } = useAuth();
  const { selectedBranch, setSelectedBranch } = useApp();
  const [vouchers, setVouchers] = useState([]);
  const [servicePlans, setServicePlans] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  // 2026-07-11 Import/Export state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [isUsageCheckOpen, setIsUsageCheckOpen] = useState(false);
  const [usageCheckCode, setUsageCheckCode] = useState('');
  const [isUseOpen, setIsUseOpen] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  // 2026-02-03: Stok voucher per paket untuk panel monitoring
  const [packageStock, setPackageStock] = useState([]);
  const [packageStockLoading, setPackageStockLoading] = useState(false);
  const _vouchersLocation = useLocation();
  const _abortCtrlRef = useRef(null);
  const _loadingTimeoutRef = useRef(null);
  const _initialStatus = (() => {
    try {
      const sp = new URLSearchParams(_vouchersLocation.search);
      const v = sp.get('status');
      return ['available','used','expired'].includes(v) ? v : 'all';
    } catch (e) { return 'all'; }
  })();
  const [statusFilter, setStatusFilter] = useState(_initialStatus);
  // Use global selectedBranch from AppContext (synced with header)
  const branchFilter = selectedBranch || 'all';
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalVouchers, setTotalVouchers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedVouchers, setSelectedVouchers] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [vouchersToPrint, setVouchersToPrint] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedMikrotik, setSelectedMikrotik] = useState(null);  // 2026-07-15: kosong dulu supaya QR/logo tidak auto-render 1000x saat buka modal
  // 2026-07-19: Cetak Batch by Tanggal — dialog form state
  const [isBatchPrintOpen, setIsBatchPrintOpen] = useState(false);
  const [batchPrintForm, setBatchPrintForm] = useState({ created_date: '', service_plan: '', owner_id: '', prefix: '', status: 'available' });
  const [batchPrintLoading, setBatchPrintLoading] = useState(false);
  const [batchPrintCount, setBatchPrintCount] = useState(null); // null=belum dihitung, number=jumlah cocok
  const [batchPrintCountLoading, setBatchPrintCountLoading] = useState(false);
  const [owners, setOwners] = useState([]);
  const printRef = useRef();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchDebounce, setSearchDebounce] = useState('');
  
  // Check if user is owner (read-only mode)
  const isOwnerRole = user?.role?.toLowerCase() === 'owner';
  const isVoucherAgent = user?.role?.toLowerCase() === 'voucher_agent';
  const isReadOnly = isOwnerRole || isVoucherAgent;
  const canManageAgent = ['admin', 'kepala_cabang'].includes((user?.role || '').toLowerCase());

  // Voucher agents list (for generate form dropdown)
  const [agents, setAgents] = useState([]);
  const _initialAgentFilter = (() => {
    try {
      const sp = new URLSearchParams(_vouchersLocation.search);
      const v = sp.get('agent_id');
      return v && v !== 'all' ? String(v) : 'all';
    } catch (e) { return 'all'; }
  })();
  const [agentFilter, setAgentFilter] = useState(_initialAgentFilter);
  useEffect(() => {
    if (canManageAgent && token) {
      axios.get(`${API_URL}/api/voucher-agents?is_active=true&limit=500`,
        { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setAgents(res.data?.data || []))
        .catch(() => setAgents([]));
    }
  }, [token, canManageAgent]);
  
  const [generateForm, setGenerateForm] = useState({
    quantity: 10,
    service_plan: '',
    branch_id: '',
    prefix: '',  // 2026-07-14: default kosong (opsional)
    code_length: 8,
    code_format: 'all_characters',
    login_method: 'password_same_as_username',
    agent_id: ''
  });
  
  const [useForm, setUseForm] = useState({
    customer_name: '',
    customer_id: '',
    router_id: ''
  });

  useEffect(() => {
    if (token) {
      fetchVouchers();
      fetchServicePlans();
      fetchBranches();
      fetchAgents();
      fetchOwners();
    }
  }, [token, currentPage, itemsPerPage, statusFilter, branchFilter, agentFilter, searchDebounce]);

  // 2026-02-03: Fetch stok voucher per paket (indipenden dari pagination).
  // Otomatis refresh saat branchFilter/agentFilter berubah + polling 60s.
  useEffect(() => {
    if (!token) return;
    let stopped = false;
    const fetchStock = async () => {
      try {
        setPackageStockLoading(true);
        const params = new URLSearchParams();
        if (branchFilter && branchFilter !== 'all') params.set('branch_id', branchFilter);
        if (agentFilter && agentFilter !== 'all') params.set('agent_id', agentFilter);
        const q = params.toString() ? `?${params.toString()}` : '';
        const res = await axios.get(`${API_URL}/api/vouchers/stock-by-package${q}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        if (!stopped && res.data?.success) {
          setPackageStock(Array.isArray(res.data.data) ? res.data.data : []);
        }
      } catch (_e) {
        // silent — panel akan menampilkan empty state
      } finally {
        if (!stopped) setPackageStockLoading(false);
      }
    };
    fetchStock();
    const t = setInterval(fetchStock, 60000);
    return () => { stopped = true; clearInterval(t); };
  }, [token, branchFilter, agentFilter]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
      setCurrentPage(1); // Reset to page 1 when searching
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 2026-07-19: Live count preview untuk Cetak Batch by Tanggal
  // Debounced 400ms — trigger hanya jika dialog terbuka & minimal 1 filter diisi.
  useEffect(() => {
    if (!isBatchPrintOpen) { setBatchPrintCount(null); return; }
    const { created_date, service_plan, owner_id, prefix, status } = batchPrintForm;
    if (!created_date && !service_plan && !owner_id && !prefix && (!status || status === 'all')) {
      setBatchPrintCount(null);
      return;
    }
    let cancelled = false;
    const ctrl = new AbortController();
    setBatchPrintCountLoading(true);
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ page: '1', limit: '1' });
        if (created_date) params.append('created_date', created_date);
        if (service_plan) params.append('service_plan', service_plan);
        if (owner_id) params.append('owner_id', owner_id);
        if (prefix) params.append('prefix', prefix.trim());
        if (status && status !== 'all') params.append('status', status);
        if (branchFilter && branchFilter !== 'all') params.append('branch_id', branchFilter);
        if (agentFilter && agentFilter !== 'all') params.append('agent_id', agentFilter);
        const res = await axios.get(`${API_URL}/api/vouchers?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ctrl.signal,
          timeout: 15000
        });
        if (!cancelled) setBatchPrintCount(res.data?.total ?? 0);
      } catch (e) {
        if (!cancelled && !axios.isCancel(e) && e.name !== 'CanceledError') {
          setBatchPrintCount(null);
        }
      } finally {
        if (!cancelled) setBatchPrintCountLoading(false);
      }
    }, 400);
    return () => { cancelled = true; ctrl.abort(); clearTimeout(timer); };
  }, [isBatchPrintOpen, batchPrintForm, token, branchFilter, agentFilter]);

  const fetchVouchers = async () => {
    // 2026-07-11 FIX: cancel any stale in-flight request to prevent race conditions
    // that leave the UI stuck on the loading spinner.
    if (_abortCtrlRef.current) {
      try { _abortCtrlRef.current.abort(); } catch (e) { /* noop */ }
    }
    const ctrl = new AbortController();
    _abortCtrlRef.current = ctrl;

    // Safety timeout: force clear loading after 20s if something goes wrong.
    if (_loadingTimeoutRef.current) clearTimeout(_loadingTimeoutRef.current);
    _loadingTimeoutRef.current = setTimeout(() => {
      setLoading(false);
      try { ctrl.abort(); } catch (e) { /* noop */ }
    }, 20000);

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (branchFilter && branchFilter !== 'all') {
        params.append('branch_id', branchFilter);
      }
      if (agentFilter && agentFilter !== 'all') {
        params.append('agent_id', agentFilter);
      }
      if (searchDebounce) {
        params.append('search', searchDebounce);
      }
      
      const response = await axios.get(`${API_URL}/api/vouchers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
        signal: ctrl.signal
      });
      
      if (response.data.success) {
        setVouchers(response.data.data || []);
        setTotalVouchers(response.data.total || 0);
        setTotalPages(response.data.total_pages || 0);
      }
    } catch (error) {
      // Skip toast for cancelled/aborted requests -- those are expected during race conditions.
      if (axios.isCancel(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error fetching vouchers:', error);
      if (error.code !== 'ECONNABORTED') {
        toast.error('Gagal memuat data voucher. Silakan coba lagi.');
      }
    } finally {
      // Only clear loading if this request is still the latest
      if (_abortCtrlRef.current === ctrl) {
        setLoading(false);
        if (_loadingTimeoutRef.current) {
          clearTimeout(_loadingTimeoutRef.current);
          _loadingTimeoutRef.current = null;
        }
      }
    }
  };

  const fetchServicePlans = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/service-plans`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      if (response.data.success) {
        // Store all plans for validity lookup
        setServicePlans(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching service plans:', error);
      // Silent fail - not critical
    }
  };

  // 2026-07-19: Fetch owners untuk dropdown Cetak Batch by Tanggal
  const fetchOwners = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/owners?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      if (response.data?.success) setOwners(response.data.data || []);
    } catch (error) {
      console.error('Error fetching owners:', error);
    }
  };
  
  // Filter hotspot plans for dropdown
  const hotspotPlans = servicePlans.filter(p => p.package_type === 'hotspot');
  
  // Format validity display
  const formatValidity = (voucher) => {
    // 2026-07-19: Voucher explicitly has validity_days=0 → unlimited (jangan fallback ke plan)
    if (voucher.validity_days !== undefined && voucher.validity_days !== null && Number(voucher.validity_days) === 0) {
      return '∞';
    }

    // Check voucher fields - MySQL uses validity_days + validity_unit
    let period = voucher.validity_days || voucher.validity_period;
    let unit = voucher.validity_unit;
    
    // If not in voucher, lookup from service plan
    if (!period || !unit) {
      const plan = servicePlans.find(p => p.name === voucher.service_plan || p.name === voucher.package_name);
      if (plan) {
        // 2026-07-19: Plan flags unlimited via validity_period=0 → treat as unlimited
        if (plan.validity_period !== undefined && plan.validity_period !== null && Number(plan.validity_period) === 0
            && (!plan.validity_days || Number(plan.validity_days) === 0)) {
          return '∞';
        }
        period = plan.validity_period || plan.validity_days;
        unit = plan.validity_unit;
      }
    }

    // Bila setelah semua fallback tetap 0/null → unlimited
    if (!period || Number(period) === 0) return '∞';
    if (!unit) unit = 'days';
    
    // Format based on unit
    if (unit === 'hours') return `${period}h`;
    if (unit === 'days') return `${period}d`;
    if (unit === 'weeks') return `${period}w`;
    if (unit === 'months') return `${period}bln`;
    
    return `${period}${unit.charAt(0)}`;
  };

  const handleExportVouchers = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (branchFilter && branchFilter !== 'all') params.append('branch_id', branchFilter);
      if (agentFilter && agentFilter !== 'all') params.append('agent_id', agentFilter);
      if (searchDebounce) params.append('search', searchDebounce);
      const res = await axios.get(`${API_URL}/api/vouchers/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `vouchers_export_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Data voucher berhasil di-export');
    } catch (e) {
      toast.error('Gagal export: ' + (e.response?.data?.detail || e.message));
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportPreview = async () => {
    if (!importFile) { toast.error('Pilih file Excel terlebih dahulu'); return; }
    setImportLoading(true);
    setImportPreview(null);
    try {
      const fd = new FormData(); fd.append('file', importFile);
      const res = await axios.post(`${API_URL}/api/vouchers/import?dry_run=true`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImportPreview(res.data);
    } catch (e) {
      toast.error('Preview gagal: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportConfirm = async () => {
    if (!importFile) { toast.error('Pilih file Excel terlebih dahulu'); return; }
    setImportLoading(true);
    try {
      const fd = new FormData(); fd.append('file', importFile);
      const res = await axios.post(`${API_URL}/api/vouchers/import`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      toast.success(`Import selesai: ${d.inserted} voucher masuk, ${d.skipped_duplicate} duplikat dilewati`);
      setIsImportOpen(false);
      setImportFile(null);
      setImportPreview(null);
      fetchVouchers();
    } catch (e) {
      toast.error('Import gagal: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImportLoading(false);
    }
  };

    const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/voucher-agents?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.data || [];
      setAgents(data.filter(a => a.is_active));
    } catch (err) {
      // silent — filter Agen tetap tampil "Semua Agen" saja
    }
  };

  const fetchBranches = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const branchData = response.data.data || [];
        setBranches(branchData);
        
        // Untuk owner, auto-set branch_id untuk generate form
        if (isOwnerRole && branchData.length > 0) {
          setGenerateForm(prev => ({ ...prev, branch_id: String(branchData[0].id) }));
        }
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-400 dark:text-slate-500" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />
      : <ArrowDown className="w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />;
  };

  const handleGenerate = async () => {
    if (!generateForm.service_plan) {
      toast.error('Pilih paket hotspot terlebih dahulu');
      return;
    }

    // Validate agent_id for non-superadmin
    if (canManageAgent && !generateForm.agent_id) {
      toast.error('Pilih Agen Voucher terlebih dahulu');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/vouchers/generate`,
        {
          ...generateForm,
          branch_id: generateForm.branch_id ? String(generateForm.branch_id) : '',
          agent_id: generateForm.agent_id ? parseInt(generateForm.agent_id, 10) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Berhasil generate ${generateForm.quantity} voucher`);
        setIsGenerateOpen(false);
        fetchVouchers();
        setGenerateForm({ quantity: 10, service_plan: '', branch_id: '', prefix: '', code_length: 8, code_format: 'all_characters', login_method: 'password_same_as_username', agent_id: '' });
      }
    } catch (error) {
      console.error('Error generating vouchers:', error);
      toast.error('Gagal generate voucher');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (voucher) => {
    setVouchersToPrint([voucher]);
    setShowPrintModal(true);
  };

  const handleBulkPrint = () => {
    if (selectedVouchers.length === 0) {
      toast.error('Pilih voucher yang ingin diprint');
      return;
    }
    
    const selectedVoucherData = vouchers.filter(v => selectedVouchers.includes(v.id));
    setVouchersToPrint(selectedVoucherData);
    setShowPrintModal(true);
  };

  // 2026-07-19: Cetak Batch by Tanggal — fetch semua voucher yang cocok filter (lintas halaman)
  const handleBatchPrint = async () => {
    const { created_date, service_plan, owner_id, prefix, status } = batchPrintForm;
    if (!created_date && !service_plan && !owner_id && !prefix && (!status || status === 'all')) {
      toast.error('Isi minimal satu filter (Status / Tanggal / Profil / Owner / Prefix)');
      return;
    }
    setBatchPrintLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', limit: '10000' });
      if (created_date) params.append('created_date', created_date);
      if (service_plan) params.append('service_plan', service_plan);
      if (owner_id) params.append('owner_id', owner_id);
      if (prefix) params.append('prefix', prefix.trim());
      if (status && status !== 'all') params.append('status', status);
      if (branchFilter && branchFilter !== 'all') params.append('branch_id', branchFilter);
      if (agentFilter && agentFilter !== 'all') params.append('agent_id', agentFilter);
      const res = await axios.get(`${API_URL}/api/vouchers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 45000
      });
      const list = res.data?.data || [];
      const total = res.data?.total || list.length;
      if (list.length === 0) {
        toast.error('Tidak ada voucher yang cocok dengan filter');
        return;
      }
      if (total > 10000) {
        toast.error(`Terlalu banyak voucher (${total}). Persempit filter (tambah Profil/Prefix/Owner).`);
        return;
      }
      setVouchersToPrint(list);
      setIsBatchPrintOpen(false);
      setShowPrintModal(true);
      toast.success(`${list.length} voucher siap dicetak`);
    } catch (error) {
      console.error('Error batch print:', error);
      toast.error('Gagal memuat voucher untuk cetak batch');
    } finally {
      setBatchPrintLoading(false);
    }
  };


  // Add header to current PDF page (company name + voucher count + date)
  const addPdfHeader = (pdf, headerText) => {
    pdf.setFontSize(8);
    pdf.setTextColor(80, 80, 80);
    pdf.text(headerText, 297 / 2, 3.2, { align: 'center' });
    pdf.setDrawColor(200, 200, 200);
    pdf.line(10, 4.2, 287, 4.2);
  };

  const downloadVoucherPdf = async () => {
    let progressToast;
    try {
      // Build header text
      const sample = vouchersToPrint[0] || {};
      const pkgName = sample.package_name || sample.plan_name || sample.profile || '';
      const priceStr = sample.price ? 'Rp ' + sample.price.toLocaleString('id-ID') : '';
      const today = new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
      const headerText = [pkgName, priceStr, vouchersToPrint.length + ' voucher', 'Cetak: ' + today].filter(Boolean).join(' - ');

      const wrapper = document.querySelector('[data-voucher-scale-wrapper]');
      if (!wrapper) { toast.error('Konten voucher tidak ditemukan'); return; }
      const target = wrapper.firstElementChild;
      if (!target) { toast.error('Template kosong'); return; }

      // Neutralize scale for accurate size
      const prevTransform = wrapper.style.transform;
      const prevWidth = wrapper.style.width;
      wrapper.style.transform = 'none';
      wrapper.style.width = 'auto';
      await new Promise(r => setTimeout(r, 100));

      const perPageMap = { modern: 25, classic: 35, 'classic-orange': 35, 'classic-blue': 35, compact: 40, elegant: 20, dense50: 50, dense70: 70, dense100: 100, simple: 35, qr: 35 };
      const perPage = perPageMap[selectedTemplate] || 25;
      const cards = Array.from(target.querySelectorAll('[data-voucher-card]'));
      if (cards.length === 0) {
        // Fallback: capture whole target if no cards found (rare, older template)
        cards.push(target);
      }
      const totalPages = Math.ceil(cards.length / perPage);
      
      // Create hidden staging container - clone same layout as target (preserves grid styles)
      const staging = target.cloneNode(false);
      // Copy computed styles that matter (width) so cards render at correct size
      staging.style.position = 'absolute';
      staging.style.left = '-99999px';
      staging.style.top = '0';
      staging.style.width = target.offsetWidth + 'px';
      staging.style.background = 'white';
      document.body.appendChild(staging);
      
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pdfW = 297;
      const pdfH = 210;
      const contentTopMm = 5;
      const maxImgHmm = pdfH - contentTopMm;
      
      progressToast = toast.loading('Menyiapkan PDF halaman 0/' + totalPages);
      
      for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
        const startCard = pageIdx * perPage;
        const endCard = Math.min(startCard + perPage, cards.length);
        
        // Populate staging with clones of this page's cards
        staging.innerHTML = '';
        for (let ci = startCard; ci < endCard; ci++) {
          staging.appendChild(cards[ci].cloneNode(true));
        }
        // Force layout
        await new Promise(r => setTimeout(r, 30));
        
        const canvas = await html2canvas(staging, {
          scale: 1.5,
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false,
        });
        const imgWmm = pdfW;
        const imgHmm = Math.min((canvas.height * imgWmm) / canvas.width, maxImgHmm);
        if (pageIdx > 0) pdf.addPage('a4', 'landscape');
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.85), 'JPEG', 0, contentTopMm, imgWmm, imgHmm, undefined, 'FAST');
        try { addPdfHeader(pdf, headerText + ' - Hal ' + (pageIdx + 1) + '/' + totalPages); } catch {}
        
        toast.loading('Menyiapkan PDF halaman ' + (pageIdx + 1) + '/' + totalPages, { id: progressToast });
        // Yield to browser
        await new Promise(r => setTimeout(r, 20));
      }
      
      // Cleanup staging + restore
      document.body.removeChild(staging);
      wrapper.style.transform = prevTransform;
      wrapper.style.width = prevWidth;
      
      const fname = 'voucher-' + selectedTemplate + '-' + new Date().toISOString().slice(0,10) + '-' + vouchersToPrint.length + 'pcs.pdf';
      pdf.save(fname);
      toast.dismiss(progressToast);
      toast.success('PDF tersimpan: ' + fname);
    } catch (e) {
      console.error('PDF generation failed:', e);
      if (progressToast) toast.dismiss(progressToast);
      toast.error('Gagal generate PDF: ' + (e?.message || 'unknown'));
    }
  };

  const executePrint = () => {
    // Temporarily remove modal wrapper scale + dialog overlays so window.print sees full-size content
    const wrappers = document.querySelectorAll('[data-voucher-scale-wrapper]');
    const prevStyles = [];
    wrappers.forEach((el) => {
      prevStyles.push({ el, transform: el.style.transform, width: el.style.width });
      el.style.transform = 'none';
      el.style.width = 'auto';
    });
    const restore = () => {
      prevStyles.forEach(({ el, transform, width }) => { el.style.transform = transform; el.style.width = width; });
      window.removeEventListener('afterprint', restore);
    };
    window.addEventListener('afterprint', restore);
    setTimeout(() => window.print(), 50);
  };

  const handleDelete = async (voucherId) => {
    if (!window.confirm('Yakin ingin menghapus voucher ini?')) return;

    try {
      const response = await axios.delete(
        `${API_URL}/api/vouchers/${voucherId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Voucher berhasil dihapus');
        fetchVouchers();
      }
    } catch (error) {
      console.error('Error deleting voucher:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus voucher');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select ALL vouchers (any status) on current page so Print/Hapus count matches Show
      setSelectedVouchers(vouchers.map(v => v.id));
    } else {
      setSelectedVouchers([]);
    }
  };

  const handleSelectVoucher = (voucherId) => {
    setSelectedVouchers(prev => {
      if (prev.includes(voucherId)) {
        return prev.filter(id => id !== voucherId);
      } else {
        return [...prev, voucherId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedVouchers.length === 0) {
      toast.error('Pilih voucher yang ingin dihapus');
      return;
    }

    setShowDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    setLoading(true);
    try {
      // 2026-02: Ganti loop serial (per-voucher DELETE) menjadi 1 request bulk-delete.
      // 500 voucher: ~4-8 menit -> ~2-4 detik.
      // Chunk 3000 per batch untuk aman di server (limit 5000).
      const ids = [...selectedVouchers];
      const CHUNK = 3000;
      let totalDeleted = 0;
      let totalRequested = 0;
      let anyFail = false;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const batch = ids.slice(i, i + CHUNK);
        try {
          const res = await axios.post(
            `${API_URL}/api/vouchers/bulk-delete`,
            { ids: batch },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          totalDeleted += (res.data?.deleted || 0);
          totalRequested += (res.data?.requested || batch.length);
        } catch (err) {
          console.error('[bulk-delete] batch failed', err);
          anyFail = true;
        }
      }
      if (totalDeleted > 0) {
        toast.success(`${totalDeleted} voucher berhasil dihapus`);
        fetchVouchers();
      }
      if (anyFail || totalDeleted < totalRequested) {
        const fail = Math.max(0, totalRequested - totalDeleted);
        if (fail > 0) toast.error(`${fail} voucher gagal dihapus`);
      }
      setSelectedVouchers([]);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error('Gagal menghapus voucher');
    } finally {
      setLoading(false);
    }
  };

  const handleUseVoucher = async () => {
    if (!selectedVoucher) return;
    
    if (!useForm.customer_name) {
      toast.error('Nama pelanggan wajib diisi');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/vouchers/${selectedVoucher.id}/use`,
        useForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const mikrotikStatus = response.data.mikrotik_status;
        if (mikrotikStatus === 'created') {
          toast.success('Voucher berhasil diaktifkan dan user dibuat di MikroTik');
        } else {
          toast.warning('Voucher diaktifkan tapi gagal membuat user di MikroTik');
        }
        
        setIsUseOpen(false);
        setUseForm({ customer_name: '', customer_id: '', router_id: '' });
        fetchVouchers();
      }
    } catch (error) {
      console.error('Error using voucher:', error);
      toast.error(error.response?.data?.detail || 'Gagal mengaktifkan voucher');
    } finally {
      setLoading(false);
    }
  };

  const openUseModal = (voucher) => {
    setSelectedVoucher(voucher);
    setUseForm({ customer_name: '', customer_id: '', router_id: '' });
    setIsUseOpen(true);
  };

  const getStatusBadge = (status, radiusDisabled = false) => {
    // If admin has disabled login for this voucher, that takes visual priority.
    if (radiusDisabled) {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800"
          title="Login voucher ini dinonaktifkan admin (Auth-Type := Reject di RADIUS)"
          data-testid="voucher-status-disabled"
        >
          <Ban className="w-3 h-3" />
          Disabled
        </span>
      );
    }
    const styles = {
      available: 'bg-green-100 text-green-800',
      used: 'bg-blue-100 text-blue-800',
      expired: 'bg-gray-100 text-gray-800'
    };
    const icons = {
      available: <Check className="w-3 h-3" />,
      used: <X className="w-3 h-3" />,
      expired: <Clock className="w-3 h-3" />
    };
    const labels = {
      available: 'Tersedia',
      used: 'Terpakai',
      expired: 'Kadaluarsa'
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {icons[status]}
        {labels[status]}
      </span>
    );
  };

  // 2026-02-03: Stats keseluruhan dari packageStock (agregasi backend) supaya
  // TERSEDIA/TERPAKAI/KADALUARSA menampilkan total riil semua voucher (bukan hanya
  // 10 rows current page). Fallback ke page-scoped count kalau packageStock belum load.
  const stats = React.useMemo(() => {
    if (packageStock && packageStock.length > 0) {
      return packageStock.reduce((acc, p) => ({
        total: acc.total + (p.total || 0),
        available: acc.available + (p.available || 0),
        used: acc.used + (p.used || 0),
        expired: acc.expired + (p.expired || 0),
      }), { total: 0, available: 0, used: 0, expired: 0 });
    }
    return {
      total: totalVouchers,
      available: vouchers.filter(v => v.status === 'available').length,
      used: vouchers.filter(v => v.status === 'used').length,
      expired: vouchers.filter(v => v.status === 'expired').length,
    };
  }, [packageStock, vouchers, totalVouchers]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 lg:p-6">
      <div className="mb-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-100">Voucher Hotspot</h1>
        <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">Kelola dan generate voucher hotspot</p>
      </div>

      {/* Stats Cards - Modern & Compact */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow dark:border-slate-700">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 dark:text-slate-400">Total</div>
          <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.total}</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1 dark:text-green-400">✓ Tersedia</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.available}</div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1 dark:text-blue-400">✓ Terpakai</div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.used}</div>
        </div>
        <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow dark:border-slate-700">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 dark:text-slate-400">⏱ Kadaluarsa</div>
          <div className="text-2xl font-bold text-slate-500 dark:text-slate-400">{stats.expired}</div>
        </div>
      </div>

      {/* 2026-02-03: Stok Voucher per Paket — panel monitoring ketersediaan cepat */}
      <div className="mb-4" data-testid="voucher-stock-by-package">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Stok Voucher per Paket</h2>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Monitor ketersediaan setiap jenis paket • diperbarui otomatis tiap 60 detik</p>
          </div>
          {packageStockLoading && (
            <div className="text-[11px] text-slate-400 flex items-center gap-1">
              <Activity className="w-3 h-3 animate-pulse" /> memuat…
            </div>
          )}
        </div>
        {packageStock.length === 0 && !packageStockLoading ? (
          <div className="bg-white dark:bg-slate-900/40 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl px-4 py-8 text-center text-xs text-slate-500 dark:text-slate-400">
            Belum ada voucher untuk cabang/agen ini. Klik <b>Generate Voucher</b> untuk mulai.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {packageStock.map((pkg, idx) => {
              const lowStock = pkg.available > 0 && pkg.available < 10;
              const outOfStock = pkg.available === 0;
              const borderCls = outOfStock
                ? 'border-rose-300 dark:border-rose-500/40'
                : lowStock
                ? 'border-amber-300 dark:border-amber-500/40'
                : 'border-slate-200 dark:border-slate-700';
              const barPctAvail = pkg.total > 0 ? Math.round((pkg.available / pkg.total) * 100) : 0;
              // 2026-02-03: pre-fill Generate dialog dengan paket dari card ini
              const handleQuickGen = () => {
                const matchedPlan = hotspotPlans.find(p => (p.name || '').toLowerCase() === (pkg.package_name || '').toLowerCase());
                setGenerateForm(prev => ({
                  ...prev,
                  service_plan: matchedPlan ? matchedPlan.name : pkg.package_name,
                  // suggest quantity supaya balik ke stock sehat (min 20)
                  quantity: outOfStock ? 20 : Math.max(20 - pkg.available, 10),
                }));
                setIsGenerateOpen(true);
              };
              return (
                <div
                  key={`${pkg.package_name}-${pkg.validity_days}-${pkg.validity_unit}-${pkg.price}-${idx}`}
                  className={`relative bg-white dark:bg-slate-900/40 border ${borderCls} rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow`}
                  data-testid={`voucher-stock-card-${idx}`}
                >
                  {outOfStock && (
                    <span className="absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-rose-500 text-white shadow">HABIS</span>
                  )}
                  {lowStock && !outOfStock && (
                    <span className="absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-500 text-white shadow">STOK RENDAH</span>
                  )}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate" title={pkg.package_name}>{pkg.package_name}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1 flex-wrap">
                        <Clock className="w-2.5 h-2.5" /> {pkg.period_label}
                        {(pkg.price_max ?? pkg.price) > 0 && (
                          <>
                            <span className="text-slate-300 dark:text-slate-600">•</span>
                            <span className="text-slate-600 dark:text-slate-300 font-medium">
                              {(pkg.price_min != null && pkg.price_max != null && pkg.price_min !== pkg.price_max)
                                ? `Rp ${Number(pkg.price_min).toLocaleString('id-ID')}–${Number(pkg.price_max).toLocaleString('id-ID')}`
                                : `Rp ${Number(pkg.price_min ?? pkg.price).toLocaleString('id-ID')}`}
                            </span>
                          </>
                        )}
                        {pkg.is_mixed && (
                          <span className="ml-1 px-1 py-0.5 rounded text-[8px] uppercase tracking-wider bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400" title="Nama paket sama tapi varian harga / masa aktif berbeda">campuran</span>
                        )}
                      </div>
                    </div>
                    <div className={`text-lg font-bold leading-none ${outOfStock ? 'text-rose-500' : lowStock ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                      {pkg.available}
                    </div>
                  </div>
                  {/* Bar progres tersedia vs total */}
                  <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full ${outOfStock ? 'bg-rose-400' : lowStock ? 'bg-amber-400' : 'bg-emerald-500'}`}
                      style={{ width: `${barPctAvail}%` }}
                    />
                  </div>
                  <div className="grid grid-cols-4 gap-1 text-center">
                    <div className="text-[9px] uppercase tracking-wider text-slate-400" title="Tersedia">
                      <div className="text-emerald-600 dark:text-emerald-400 font-semibold text-xs">{pkg.available}</div>
                      Ready
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-slate-400" title="Terpakai">
                      <div className="text-blue-500 dark:text-blue-400 font-semibold text-xs">{pkg.used}</div>
                      Pakai
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-slate-400" title="Kadaluarsa">
                      <div className="text-slate-500 dark:text-slate-400 font-semibold text-xs">{pkg.expired}</div>
                      Exp
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-slate-400" title="Total">
                      <div className="text-slate-700 dark:text-slate-200 font-semibold text-xs">{pkg.total}</div>
                      Total
                    </div>
                  </div>
                  {/* Quick Generate button */}
                  <button
                    type="button"
                    onClick={handleQuickGen}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 bg-white/60 dark:bg-slate-800/60 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-300 dark:hover:border-emerald-400/60 active:scale-[0.97] transition"
                    data-testid={`voucher-stock-generate-${idx}`}
                    title={`Generate voucher paket ${pkg.package_name}`}
                  >
                    <Plus className="w-3 h-3" strokeWidth={2.5} />
                    Generate
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Filters and Actions - Compact */}
      <Card className="mb-4 shadow-sm">
        <CardContent className="p-3">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex gap-2 flex-1 items-center flex-wrap">
              {/* Search */}
              <Input
                type="text"
                placeholder="🔍 Cari voucher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-xs h-9 text-sm"
              />

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm h-9 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:border-slate-600"
              >
                <option value="all">Semua Status</option>
                <option value="available">Tersedia</option>
                <option value="used">Terpakai</option>
                <option value="expired">Kadaluarsa</option>
              </select>

              {/* Branch Filter - Synced with header dropdown */}
              <select
                value={branchFilter}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className={`px-3 py-1.5 border border-slate-300 rounded-lg text-sm h-9 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${isOwnerRole ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                disabled={isOwnerRole}
              >
                {isOwnerRole ? (
                  // Owner: hanya tampilkan branch miliknya
                  branches.filter(b => b.is_active).map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))
                ) : (
                  // Admin: tampilkan semua dengan default "Semua Cabang"
                  <>
                    <option value="all">Semua Cabang</option>
                    {branches.filter(b => b.is_active).map(branch => (
                      <option key={branch.id} value={branch.id}>{branch.name}</option>
                    ))}
                  </>
                )}
              </select>

              {/* Agent Filter - visible only for admin/kepala_cabang, not for voucher_agent role */}
              {(user?.role || '').toLowerCase() !== 'voucher_agent' && (
                <select
                  value={agentFilter}
                  onChange={(e) => { setAgentFilter(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm h-9 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                  data-testid="agent-filter"
                >
                  <option value="all">Semua Agen</option>
                  <option value="none">— Tanpa Agen —</option>
                  {agents.map(a => (
                    <option key={a.id} value={a.id}>{a.name}{a.branch_name ? ` (${a.branch_name})` : ''}</option>
                  ))}
                </select>
              )}

              {/* Bulk Actions */}
              {selectedVouchers.length > 0 && (
                <>
                  <Button 
                    onClick={handleBulkPrint}
                    variant="outline"
                    size="sm"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50 h-9 dark:text-blue-400 dark:hover:bg-blue-900/30"
                  >
                    <Printer className="w-4 h-4 mr-1" />
                    Print ({selectedVouchers.length})
                  </Button>
                  {!isReadOnly && (
                  <Button 
                    onClick={handleBulkDelete}
                    variant="destructive"
                    size="sm"
                    className="h-9"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Hapus ({selectedVouchers.length})
                  </Button>
                  )}
                </>
              )}
            </div>

            <Button
              onClick={() => setIsUsageCheckOpen(true)}
              variant="outline"
              className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-500 hover:text-white h-9 shadow-sm"
              data-testid="usage-check-btn"
            >
              <SearchIcon className="w-4 h-4 mr-1" />
              Cek Pemakaian
            </Button>
            <Button
              onClick={handleExportVouchers}
              disabled={exportLoading}
              variant="outline"
              className="border-2 border-sky-500 text-sky-600 hover:bg-sky-500 hover:text-white h-9 shadow-sm"
              data-testid="vouchers-export-btn"
              title="Export ke Excel (respect filter aktif)"
            >
              <Download className="w-4 h-4 mr-1" />
              {exportLoading ? 'Menyiapkan…' : 'Export'}
            </Button>
            {!isReadOnly && (
            <Button
              onClick={() => { setIsImportOpen(true); setImportPreview(null); setImportFile(null); }}
              variant="outline"
              className="border-2 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white h-9 shadow-sm"
              data-testid="vouchers-import-btn"
              title="Import dari Excel (skip duplikat, auto-sync RADIUS)"
            >
              <UploadIcon className="w-4 h-4 mr-1" />
              Import
            </Button>
            )}
            {!isReadOnly && (
            <Button
              onClick={() => { setIsBatchPrintOpen(true); setBatchPrintForm({ created_date: '', service_plan: '', owner_id: '', prefix: '', status: 'available' }); }}
              variant="outline"
              className="border-2 border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white h-9 shadow-sm"
              data-testid="batch-print-btn"
              title="Cetak voucher massal berdasarkan Tanggal Dibuat + Profil + Owner + Prefix"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Cetak Batch by Tanggal
            </Button>
            )}
                        {!isReadOnly && (
            <Button 
              onClick={() => setIsGenerateOpen(true)}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary hover:text-white h-9 shadow-sm"
              data-testid="generate-voucher-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Generate Voucher
            </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Vouchers Table - Modern & Compact */}
      <Card className="shadow-sm">
        <CardContent className="p-0">
          {/* Pagination Controls - Compact */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b bg-slate-50 dark:bg-slate-900">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600 dark:text-slate-300">Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border border-slate-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="500">500</option>
                <option value="1000">1000</option>
                <option value="2000">2000</option>
                <option value="3000">3000</option>
              </select>
            </div>
            <div className="text-xs text-slate-600 dark:text-slate-300">
              {totalVouchers > 0 ? `${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, totalVouchers)} of ${totalVouchers}` : '0 voucher'}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-slate-500 dark:text-slate-400">Memuat data voucher...</span>
              </div>
            </div>
          )}

          {!loading && (
          <div className="overflow-auto max-h-[calc(100vh-360px)] custom-scrollbar-blue">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-3 py-2.5 text-left w-10">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={
                        selectedVouchers.length > 0 &&
                        vouchers.every(v => selectedVouchers.includes(v.id))
                      }
                      className="w-4 h-4 rounded border-slate-300 dark:border-slate-600"
                    />
                  </th>
                  <th 
                    className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors dark:text-slate-200"
                    onClick={() => handleSort('voucher_code')}
                  >
                    <div className="flex items-center">
                      Kode Voucher
                      {getSortIcon('voucher_code')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors dark:text-slate-200"
                    onClick={() => handleSort('username')}
                  >
                    <div className="flex items-center">
                      Username
                      {getSortIcon('username')}
                    </div>
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">Password</th>
                  <th 
                    className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors dark:text-slate-200"
                    onClick={() => handleSort('service_plan')}
                  >
                    <div className="flex items-center">
                      Paket
                      {getSortIcon('service_plan')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors dark:text-slate-200"
                    onClick={() => handleSort('price')}
                  >
                    <div className="flex items-center">
                      Harga
                      {getSortIcon('price')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors dark:text-slate-200"
                    onClick={() => handleSort('validity_days')}
                  >
                    <div className="flex items-center">
                      Masa Aktif
                      {getSortIcon('validity_days')}
                    </div>
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">Cabang</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">Agen</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">Dibuat Oleh</th>
                  <th 
                    className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors dark:text-slate-200"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      {getSortIcon('status')}
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors whitespace-nowrap dark:text-slate-200"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Dibuat
                      {getSortIcon('created_at')}
                    </div>
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((voucher) => (
                  <tr key={voucher.id} className="border-b border-slate-100 hover:bg-indigo-50/30 transition-colors dark:border-slate-700">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedVouchers.includes(voucher.id)}
                        onChange={() => handleSelectVoucher(voucher.id)}
                        className="w-4 h-4 rounded border-slate-300 dark:border-slate-600"
                      />
                    </td>
                    <td className="px-3 py-2 text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400">
                      {voucher.voucher_code}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-700 dark:text-slate-200">{voucher.username}</td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-500 dark:text-slate-400">{voucher.password}</td>
                    <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-200">{voucher.service_plan}</td>
                    <td className="px-3 py-2 text-xs font-semibold text-slate-900 dark:text-slate-100">Rp {voucher.price?.toLocaleString('id-ID') || 0}</td>
                    <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-200">{formatValidity(voucher)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{voucher.branch_name || '-'}</td>
                    <td className="px-3 py-2 text-xs">
                      {voucher.agent_name ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" title="Agen Voucher">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          {voucher.agent_name}
                        </span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{voucher.created_by_name || voucher.owner_name || '-'}</td>
                    <td className="px-3 py-2">{getStatusBadge(voucher.status, voucher.radius_disabled)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap tabular-nums dark:text-slate-300">
                      {voucher.created_at ? (() => {
                        try {
                          const d = new Date(voucher.created_at.replace(' ', 'T'));
                          const pad = (n) => String(n).padStart(2, '0');
                          return `${pad(d.getDate())}/${pad(d.getMonth()+1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                        } catch { return voucher.created_at; }
                      })() : '-'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        {voucher.status === 'available' && (
                          <button
                            onClick={() => openUseModal(voucher)}
                            className="p-1.5 text-green-600 hover:bg-green-100 rounded-md transition-colors dark:text-green-400 dark:hover:bg-green-900/40"
                            title="Gunakan Voucher"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => handlePrint(voucher)}
                          className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-md transition-colors dark:text-blue-400 dark:hover:bg-blue-900/40"
                          title="Print"
                        >
                          <Printer className="w-3.5 h-3.5" />
                        </button>
                        {/* 2026-07-31: Cek Pemakaian row — auto-fill code ke modal */}
                        <button
                          onClick={() => { setUsageCheckCode(voucher.voucher_code); setIsUsageCheckOpen(true); }}
                          className="p-1.5 text-indigo-600 hover:bg-indigo-100 rounded-md transition-colors dark:text-indigo-400 dark:hover:bg-indigo-900/40"
                          title="Cek Pemakaian"
                          data-testid={`voucher-cek-${voucher.id}`}
                        >
                          <Activity className="w-3.5 h-3.5" />
                        </button>
                        {voucher.status === 'available' && !isReadOnly && (
                          <button
                            onClick={() => handleDelete(voucher.id)}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-md transition-colors dark:text-red-400 dark:hover:bg-red-900/40"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {!loading && vouchers.length === 0 && (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400">
              Tidak ada voucher. Klik &quot;Generate Voucher&quot; untuk membuat.
            </div>
          )}

          {/* Pagination Navigation */}
          {!loading && totalVouchers > 0 && (
            <div className="flex items-center justify-between p-4 border-t">
              <Button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
              >
                Previous
              </Button>
              <span className="text-sm text-slate-600 dark:text-slate-300">
                Halaman {currentPage} dari {totalPages}
              </span>
              <Button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage >= totalPages}
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Modal */}

      <VoucherUsageCheckModal open={isUsageCheckOpen} onOpenChange={(v) => { setIsUsageCheckOpen(v); if (!v) setUsageCheckCode(""); }} initialCode={usageCheckCode} onSuccess={fetchVouchers} />

      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent data-testid="vouchers-import-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Voucher dari Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 p-3 rounded-md">
              <b>Format yang didukung:</b> minimal kolom <code>Login</code> (kode) + <code>Password</code>.
              Kolom tambahan yang dikenali: <code>Plan/Paket, Validity (mis. "15 H"), Price/Harga, Created, Expired</code>.
              Duplikat kode akan otomatis di-skip. Auto-sync ke RADIUS setelah insert.
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Pilih file Excel (.xlsx)</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                data-testid="vouchers-import-file-input"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportPreview(null); }}
                className="block w-full text-sm border rounded-md p-1.5"
              />
              {importFile && <div className="text-xs text-slate-500 mt-1">Dipilih: {importFile.name} ({(importFile.size/1024).toFixed(1)} KB)</div>}
            </div>
            {importPreview && (
              <div className="border rounded-md p-3 bg-blue-50 dark:bg-blue-900/20 text-xs space-y-1">
                <div><b>Preview Import:</b></div>
                <div>• Total baris: <b>{importPreview.total_rows}</b></div>
                <div>• Akan dimasukkan: <b className="text-emerald-600">{importPreview.will_insert}</b></div>
                <div>• Duplikat dilewati: <b className="text-amber-600">{importPreview.skipped_duplicate}</b></div>
                {importPreview.errors?.length > 0 && (
                  <div className="text-rose-600">• Errors: {importPreview.errors.length} (contoh: {importPreview.errors[0]})</div>
                )}
                {importPreview.sample?.length > 0 && (
                  <div className="mt-1 pt-1 border-t border-blue-200 dark:border-blue-800">
                    <div className="font-semibold mb-1">Contoh baris:</div>
                    <div className="max-h-24 overflow-auto text-[10px] font-mono">
                      {importPreview.sample.map((s, i) => (
                        <div key={i} className="truncate">
                          {s.code} | {s.package_name} | {s.validity_days}{s.validity_unit?.[0] || ''} | Rp{s.price}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsImportOpen(false)} disabled={importLoading} data-testid="vouchers-import-cancel">
              Batal
            </Button>
            {!importPreview ? (
              <Button onClick={handleImportPreview} disabled={importLoading || !importFile} data-testid="vouchers-import-preview-btn">
                {importLoading ? 'Memproses…' : 'Preview'}
              </Button>
            ) : (
              <Button onClick={handleImportConfirm} disabled={importLoading || importPreview.will_insert === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="vouchers-import-confirm-btn">
                {importLoading ? 'Meng-import…' : `Konfirmasi Import ${importPreview.will_insert} Voucher`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

            <Dialog open={isGenerateOpen} onOpenChange={setIsGenerateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Voucher Hotspot</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Jumlah Voucher *</Label>
              <Input
                type="number"
                value={generateForm.quantity}
                onChange={(e) => setGenerateForm({...generateForm, quantity: parseInt(e.target.value)})}
                min="1"
                max="100"
              />
            </div>

            <div>
              <Label>Paket Hotspot *</Label>
              <select
                value={generateForm.service_plan}
                onChange={(e) => setGenerateForm({...generateForm, service_plan: e.target.value})}
                className="w-full p-2.5 border border-slate-300 rounded-lg dark:border-slate-600"
              >
                <option value="">Pilih Paket</option>
                {hotspotPlans.map(plan => (
                  <option key={plan.id} value={plan.name}>
                    {plan.name} - Rp {plan.price.toLocaleString('id-ID')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Cabang {isOwnerRole ? '' : '(Opsional)'}</Label>
              <select
                value={generateForm.branch_id}
                onChange={(e) => setGenerateForm({...generateForm, branch_id: e.target.value})}
                className={`w-full p-2.5 border border-slate-300 rounded-lg ${isOwnerRole ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                disabled={isOwnerRole}
              >
                {isOwnerRole ? (
                  // Owner: hanya tampilkan branch miliknya (sudah di-filter)
                  branches.filter(b => b.is_active).map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))
                ) : (
                  // Admin/lainnya: tampilkan semua
                  <>
                    <option value="">Semua Cabang</option>
                    {branches.filter(b => b.is_active).map(branch => (
                      <option key={branch.id} value={branch.id}>{branch.name}</option>
                    ))}
                  </>
                )}
              </select>
            </div>

            <div>
              <Label>Prefix Kode <span className="text-slate-400 font-normal">(opsional)</span></Label>
              <Input
                value={generateForm.prefix}
                onChange={(e) => setGenerateForm({...generateForm, prefix: e.target.value.toUpperCase()})}
                maxLength="4"
                placeholder="Kosongkan jika tidak perlu"
              />
              <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Kosongkan untuk voucher tanpa prefix (angka saja). Contoh dengan prefix: HS-ABC12345</p>
            </div>

            {canManageAgent && (
              <div>
                <Label className="text-amber-500 font-semibold">Assign ke Agen Voucher <span className="text-red-500">*</span></Label>
                <select
                  value={generateForm.agent_id || ''}
                  onChange={(e) => setGenerateForm({...generateForm, agent_id: e.target.value})}
                  className="w-full p-2.5 border border-slate-300 rounded-lg"
                  data-testid="generate-agent-select"
                >
                  <option value="">-- Pilih Agen --</option>
                  {agents.length === 0 && <option value="" disabled>Belum ada agen. Buat di menu Agen Voucher terlebih dahulu.</option>}
                  {agents.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {a.commission_type === 'percent'
                        ? ` — komisi ${a.commission_value}%`
                        : ` — komisi Rp${Number(a.commission_value).toLocaleString('id-ID')}/voucher`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">Semua voucher dalam batch ini akan di-assign ke agen yang dipilih.</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Panjang Kode</Label>
                <Input
                  type="number"
                  value={generateForm.code_length}
                  onChange={(e) => setGenerateForm({...generateForm, code_length: parseInt(e.target.value)})}
                  min="4"
                  max="12"
                />
                <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Min: 4, Max: 12</p>
              </div>

              <div>
                <Label>Metode Login</Label>
                <select
                  value={generateForm.login_method}
                  onChange={(e) => setGenerateForm({...generateForm, login_method: e.target.value})}
                  className="w-full p-2.5 border border-slate-300 rounded-lg text-sm dark:border-slate-600"
                >
                  <option value="password_different">Password ≠ Username</option>
                  <option value="password_same_as_username">Password = Username</option>
                </select>
              </div>
            </div>

            <div>
              <Label>Kombinasi Kode</Label>
              <select
                value={generateForm.code_format}
                onChange={(e) => setGenerateForm({...generateForm, code_format: e.target.value})}
                className="w-full p-2.5 border border-slate-300 rounded-lg dark:border-slate-600"
              >
                <option value="uppercase_only">1. Huruf Besar Saja (ABCDEFGH)</option>
                <option value="lowercase_only">2. Huruf Kecil Saja (abcdefgh)</option>
                <option value="mixed_case">3. Huruf Besar + Kecil (AbCdEfGh)</option>
                <option value="number_only">4. Angka Saja (12345678)</option>
                <option value="uppercase_number">5. Huruf Besar + Angka (ABC12345)</option>
                <option value="lowercase_number">6. Huruf Kecil + Angka (abc12345)</option>
                <option value="all_characters">7. Gabungan Semua (Ab1Cd2Ef)</option>
              </select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                onClick={handleGenerate}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600"
              >
                {loading ? 'Generating...' : 'Generate'}
              </Button>
              <Button 
                onClick={() => setIsGenerateOpen(false)}
                variant="outline"
                className="flex-1"
              >
                Batal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Use Voucher Modal */}
      <Dialog open={isUseOpen} onOpenChange={setIsUseOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Gunakan Voucher</DialogTitle>
          </DialogHeader>
          {selectedVoucher && (
            <div className="space-y-4">
              <div className="bg-indigo-50 p-3 rounded-lg dark:bg-indigo-900/30">
                <p className="text-sm text-slate-600 dark:text-slate-300">Kode Voucher</p>
                <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{selectedVoucher.voucher_code}</p>
                <p className="text-sm text-slate-600 mt-2 dark:text-slate-300">Paket: {selectedVoucher.service_plan}</p>
              </div>

              <div>
                <Label>Nama Pelanggan *</Label>
                <Input
                  type="text"
                  value={useForm.customer_name}
                  onChange={(e) => setUseForm({...useForm, customer_name: e.target.value})}
                  placeholder="Masukkan nama pelanggan"
                />
              </div>

              <div>
                <Label>ID Pelanggan (Opsional)</Label>
                <Input
                  type="text"
                  value={useForm.customer_id}
                  onChange={(e) => setUseForm({...useForm, customer_id: e.target.value})}
                  placeholder="ID pelanggan jika ada"
                />
              </div>

              <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-800 dark:bg-yellow-900/30">
                ⚠️ Voucher akan diaktifkan dan user akan dibuat di MikroTik router
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={handleUseVoucher}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600"
                >
                  {loading ? 'Processing...' : 'Aktifkan Voucher'}
                </Button>
                <Button 
                  onClick={() => setIsUseOpen(false)}
                  variant="outline"
                  className="flex-1"
                  disabled={loading}
                >
                  Batal
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Konfirmasi Hapus Voucher</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600 dark:text-slate-300">
              Anda akan menghapus <span className="font-bold text-red-600 dark:text-red-400">{selectedVouchers.length}</span> voucher.
              Apakah Anda yakin?
            </p>
            <div className="bg-red-50 p-3 rounded-lg text-sm text-red-800 dark:bg-red-900/30">
              ⚠️ Voucher yang sudah dihapus tidak dapat dikembalikan
            </div>
            <div className="flex gap-2 pt-4">
              <Button 
                onClick={confirmBulkDelete}
                disabled={loading}
                variant="destructive"
                className="flex-1"
              >
                {loading ? 'Menghapus...' : 'Ya, Hapus'}
              </Button>
              <Button 
                onClick={() => setShowDeleteConfirm(false)}
                variant="outline"
                className="flex-1"
                disabled={loading}
              >
                Batal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2026-07-19: Cetak Batch by Tanggal — dialog form filter */}
      <Dialog open={isBatchPrintOpen} onOpenChange={setIsBatchPrintOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-purple-600" />
              Cetak Batch Voucher
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-900 dark:bg-blue-900/30" data-testid="batch-print-hint">
              <div className="mb-1">💡 <b>Cetak voucher massal berdasarkan filter:</b></div>
              <ul className="list-disc pl-5 text-xs space-y-0.5">
                <li><b>Status</b> — default: <i>Tersedia</i> (belum dipakai)</li>
                <li><b>Tanggal Dibuat</b> — kosongkan untuk semua tanggal</li>
                <li><b>Profil</b> — kosongkan untuk semua profil</li>
                <li><b>Owner Data</b> — kosongkan untuk semua owner</li>
                <li><b>Prefix Voucher</b> — kosongkan untuk semua prefix</li>
              </ul>
              <div className="mt-1 text-xs">Isi minimal 1 filter. Semua voucher yang cocok akan dicetak sekaligus (max 10.000).</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold mb-1.5 block">Status Voucher</Label>
                <select
                  value={batchPrintForm.status}
                  onChange={(e) => setBatchPrintForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full p-2 border border-slate-300 rounded-lg text-sm h-10 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                  data-testid="batch-print-status"
                >
                  <option value="all">Semua Status</option>
                  <option value="available">Tersedia (Belum Dipakai)</option>
                  <option value="used">Terpakai</option>
                  <option value="expired">Kadaluarsa</option>
                </select>
              </div>

              <div>
                <Label className="text-sm font-semibold flex items-center gap-1 mb-1.5">
                  <Calendar className="w-3.5 h-3.5" /> Tanggal Dibuat
                </Label>
                <Input
                  type="date"
                  value={batchPrintForm.created_date}
                  onChange={(e) => setBatchPrintForm(f => ({ ...f, created_date: e.target.value }))}
                  data-testid="batch-print-date"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold mb-1.5 block">Profil (Paket Hotspot)</Label>
                <select
                  value={batchPrintForm.service_plan}
                  onChange={(e) => setBatchPrintForm(f => ({ ...f, service_plan: e.target.value }))}
                  className="w-full p-2 border border-slate-300 rounded-lg text-sm h-10 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                  data-testid="batch-print-plan"
                >
                  <option value="">-- Semua Profil --</option>
                  {hotspotPlans.map(p => (
                    <option key={p.id} value={p.name}>{p.name} (Rp {Number(p.price || 0).toLocaleString('id-ID')})</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-sm font-semibold mb-1.5 block">Owner Data</Label>
                <select
                  value={batchPrintForm.owner_id}
                  onChange={(e) => setBatchPrintForm(f => ({ ...f, owner_id: e.target.value }))}
                  className="w-full p-2 border border-slate-300 rounded-lg text-sm h-10 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                  data-testid="batch-print-owner"
                >
                  <option value="">-- Semua Owner --</option>
                  {owners.map(o => (
                    <option key={o.id} value={o.id}>{o.name}{o.owner_code ? ` (${o.owner_code})` : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-sm font-semibold mb-1.5 block">Prefix Voucher <span className="text-xs text-slate-500 font-normal">(Opsional)</span></Label>
                <Input
                  type="text"
                  placeholder="mis: UZ, KH, RJ..."
                  value={batchPrintForm.prefix}
                  onChange={(e) => setBatchPrintForm(f => ({ ...f, prefix: e.target.value }))}
                  data-testid="batch-print-prefix"
                />
              </div>
            </div>

            {/* Live count preview */}
            <div className="rounded-lg border-2 border-dashed px-4 py-3 text-sm flex items-center justify-between gap-3 min-h-[52px]" data-testid="batch-print-count-box"
              style={{
                borderColor: batchPrintCount === null ? '#e2e8f0' : (batchPrintCount === 0 ? '#fca5a5' : batchPrintCount > 10000 ? '#fdba74' : '#86efac'),
                background: batchPrintCount === null ? '#f8fafc' : (batchPrintCount === 0 ? '#fef2f2' : batchPrintCount > 10000 ? '#fff7ed' : '#f0fdf4'),
              }}>
              <div className="flex items-center gap-2">
                <SearchIcon className="w-4 h-4 text-slate-500" />
                {batchPrintCount === null ? (
                  <span className="text-slate-500">Isi filter untuk melihat jumlah voucher yang cocok...</span>
                ) : batchPrintCountLoading ? (
                  <span className="text-slate-600">Menghitung...</span>
                ) : batchPrintCount === 0 ? (
                  <span className="text-red-700 font-semibold">Tidak ada voucher yang cocok</span>
                ) : batchPrintCount > 10000 ? (
                  <span className="text-orange-800 font-semibold">
                    {batchPrintCount.toLocaleString('id-ID')} voucher cocok — melebihi batas 10.000. Persempit filter dulu.
                  </span>
                ) : batchPrintCount > 5000 ? (
                  <span className="text-amber-700 font-semibold">
                    {batchPrintCount.toLocaleString('id-ID')} voucher cocok — cetakan besar, mungkin lambat (⏱ ~1-2 menit).
                  </span>
                ) : (
                  <span className="text-emerald-800 font-bold">
                    {batchPrintCount.toLocaleString('id-ID')} voucher cocok filter
                  </span>
                )}
              </div>
              {batchPrintCountLoading && (
                <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setIsBatchPrintOpen(false)}
              disabled={batchPrintLoading}
              data-testid="batch-print-cancel"
            >
              Batal
            </Button>
            <Button
              onClick={handleBatchPrint}
              disabled={batchPrintLoading || batchPrintCountLoading || batchPrintCount === 0 || (batchPrintCount !== null && batchPrintCount > 10000)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="batch-print-submit"
            >
              <Printer className="w-4 h-4 mr-1" />
              {batchPrintLoading ? 'Memuat voucher...' : (batchPrintCount !== null && batchPrintCount > 0 && batchPrintCount <= 10000 ? `Lanjutkan Cetak (${batchPrintCount.toLocaleString('id-ID')})` : 'Lanjutkan Cetak')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Modal */}
      <Dialog open={showPrintModal} onOpenChange={setShowPrintModal}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Preview Print Voucher ({vouchersToPrint.length})</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Template Selector */}
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg dark:bg-blue-900/30">
              <Label className="block text-sm font-semibold mb-2 text-blue-900">
                🎨 Pilih Template Voucher
              </Label>
                            <MikrotikGatewaySelector value={selectedMikrotik} onChange={setSelectedMikrotik} />
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                data-testid="print-template-select"
              >
                <option value="">-- Pilih Template Voucher --</option>
                <option value="simple">⚡ Template Ringan: Simple Text (35/hal - RECOMMENDED untuk 1000+ voucher)</option>
                <option value="modern">Template 1: Modern (dengan shadow & gradient)</option>
                <option value="classic">Template 2: Classic Hijau (35 voucher - Minimalis)</option>
                <option value="classic-orange">Template 3: Classic Orange (35 voucher - Warna Logo)</option>
                <option value="classic-blue">Template 4: Classic Biru (35 voucher - Warna Biru)</option>
                <option value="compact">Template 5: Compact (Simple & Clean)</option>
                <option value="elegant">Template 6: Elegant (Premium & Mewah)</option>
                <option value="dense50">Template 7: Dense (50 voucher - 10×5 padat)</option>
                <option value="dense70">Template 8: Ultra Dense (70 voucher - 10×7 sangat padat)</option>
                <option value="dense100">Template 9: Mega Dense (100 voucher - 10×10 super padat)</option>
                <option value="qr">Template 10: QR Code (35 voucher/halaman - dengan QR Code scan)</option>
              </select>
              <p className="text-xs text-blue-700 mt-2 dark:text-blue-300">
                💡 Setiap template memiliki style dan layout yang berbeda
              </p>
            </div>
            
            <div className="bg-red-50 border-2 border-red-400 p-3 rounded-lg text-sm text-red-900 font-semibold mb-2 dark:bg-red-900/30" data-testid="print-warning"><div className="mb-1">⚠️ <b>PENTING - Setting Print Dialog Browser</b></div><div className="text-xs font-normal text-red-800">Pada dialog Print/Save PDF, WAJIB pilih: <b>Orientation = Landscape (Horizontal)</b> &nbsp;•&nbsp; <b>Paper = A4</b> &nbsp;•&nbsp; <b>Margins = None / Minimum</b> &nbsp;•&nbsp; ✔ <b>Background graphics</b></div></div>
            
            
            
            <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-800 dark:bg-yellow-900/30">
              ℹ️ Layout A4: {(selectedTemplate === 'dense100' ? Math.ceil(vouchersToPrint.length / 100) : selectedTemplate === 'dense70' ? Math.ceil(vouchersToPrint.length / 70) : selectedTemplate === 'dense50' ? Math.ceil(vouchersToPrint.length / 50) : selectedTemplate === 'compact' ? Math.ceil(vouchersToPrint.length / 40) : ['classic', 'classic-orange', 'classic-blue', 'simple', 'qr'].includes(selectedTemplate) ? Math.ceil(vouchersToPrint.length / 35) : Math.ceil(vouchersToPrint.length / 25))} halaman • {vouchersToPrint.length} voucher • {(selectedTemplate === 'dense100' ? '100' : selectedTemplate === 'dense70' ? '70' : selectedTemplate === 'dense50' ? '50' : selectedTemplate === 'compact' ? '40' : ['classic', 'classic-orange', 'classic-blue', 'simple', 'qr'].includes(selectedTemplate) ? '35' : '25')} voucher per halaman
            </div>
            
            {/* 2026-07-15: Only render VoucherPrintTemplate after user picks a template - avoids 1000× QR/logo generation on modal open */}
            <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-slate-50 px-2 py-3 flex justify-center dark:border-slate-700 dark:bg-slate-900 min-h-[240px]">
              {selectedTemplate ? (
                <div data-voucher-scale-wrapper style={{ transform: 'scale(0.72)', transformOrigin: 'top center', width: '1122px' }}>
                  <VoucherPrintTemplate vouchers={vouchersToPrint} template={selectedTemplate} hotspotGatewayIp={selectedMikrotik?.hotspot_gateway_ip} />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="preview-empty-placeholder">
                  <div className="text-5xl mb-3">🎨</div>
                  <div className="text-lg font-semibold text-slate-700 dark:text-slate-200">Pilih template dulu di dropdown atas</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">Preview {vouchersToPrint.length} voucher akan muncul setelah template dipilih</div>
                </div>
              )}
            </div>

            {/* 2026-07-15: Hint for PDF - browser native handles it faster than html2canvas for large batches */}
            {selectedTemplate && vouchersToPrint.length > 100 && (
              <div className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg text-xs" data-testid="pdf-tip">
                <div className="font-semibold text-emerald-800 dark:text-emerald-300 mb-1">💡 Tip cetak ke PDF:</div>
                <div className="text-emerald-700 dark:text-emerald-400">
                  Klik <b>Cetak Sekarang</b> → di jendela printer pilih <b>&quot;Save as PDF&quot;</b> / <b>&quot;Microsoft Print to PDF&quot;</b> → Save. 
                  Lebih cepat & hasil identik seperti tampilan preview.
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button 
                onClick={executePrint}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={!selectedTemplate}
                data-testid="btn-print-now"
              >
                <Printer className="w-4 h-4 mr-2" />
                Cetak Sekarang / Simpan ke PDF
              </Button>
              <Button 
                onClick={() => setShowPrintModal(false)}
                variant="outline"
                className="flex-1"
                data-testid="btn-cancel-print"
              >
                Batal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2026-07-15: Hidden print div DELETED - was double-rendering 1000 vouchers (executePrint uses window.print directly on modal) */}
    </div>
  );
};

export default Vouchers;
